import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Eye, ShieldCheck } from 'lucide-react';
import '../components/aiinterview/aiinterview.css';
import '../components/scheduling/scheduling.css';
import { errMsg } from '../lib/aiInterviewApi';
import { shareAPI } from '../lib/schedulingApi';
import { fmtDate, Pill, Section, TierPill } from '../components/aiinterview/recruiter/common';
import { AdvisoryBanner } from '../components/aiinterview/report/AdvisoryDecision';
import CompetencyScores from '../components/aiinterview/report/CompetencyScores';
import { ClaimMatrix, InsightsPanel } from '../components/aiinterview/report/ClaimsInsights';
import RecordingTranscript from '../components/aiinterview/report/RecordingTranscript';

const KEY = (t) => `chire_share_${t}`;

const Shell = ({ children, meta }) => (
  <div className="iv iv-page" data-testid="shared-report-page">
    <header className="iv-top">
      <div className="iv-row" style={{ gap: '0.6rem' }}>
        <img src="/chire-icon.png" alt="CHIRE" width={28} height={28} style={{ borderRadius: 6 }} />
        <span style={{ fontWeight: 800, letterSpacing: '0.02em' }}>CHIRE <span style={{ fontWeight: 500, opacity: 0.7 }}>Shared report</span></span>
      </div>
      {meta && <div className="iv-top-meta"><Eye size={12} style={{ display: 'inline', verticalAlign: '-1px' }} /> Read-only · expires {fmtDate(meta.expires_at)}</div>}
    </header>
    <main className="iv-rpage" style={{ paddingTop: '1.25rem' }}>{children}</main>
  </div>
);

function Gate({ token, meta, onVerified }) {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { await shareAPI.requestCode(token, email); setSent(true); toast.success('If this email is allowed, a code is on its way'); } catch (e) { toast.error(errMsg(e, 'Could not send a code')); } finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true);
    try { const { data } = await shareAPI.verify(token, email, code); sessionStorage.setItem(KEY(token), data.session); onVerified(data.session); } catch (e) { toast.error(errMsg(e, 'That code did not work')); } finally { setBusy(false); }
  };
  return (
    <div className="iv-card" style={{ maxWidth: 460, margin: '2rem auto' }} data-testid="share-gate">
      <div className="iv-eyebrow"><ShieldCheck size={13} style={{ display: 'inline', verticalAlign: '-2px' }} /> Confirm it's you</div>
      <h1 className="iv-h2" style={{ marginTop: 6 }}>{meta.job_title}</h1>
      <p className="iv-sub" style={{ fontSize: '0.9rem' }}>{meta.shared_by || 'The hiring team'} shared an interview report with specific people. Enter your work email to get a one-time code.</p>
      <div style={{ display: 'grid', gap: '0.6rem', marginTop: '0.9rem' }}>
        <input className="iv-input" type="email" placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)} disabled={sent} data-testid="share-gate-email" />
        {!sent ? (
          <button type="button" className="iv-btn iv-btn-primary" disabled={busy || !email.includes('@')} onClick={send} data-testid="share-gate-send-btn">Email me a code</button>
        ) : (
          <>
            <input className="iv-input sc-code" inputMode="numeric" maxLength={6} placeholder="••••••" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} data-testid="share-gate-code" />
            <button type="button" className="iv-btn iv-btn-primary" disabled={busy || code.length !== 6} onClick={verify} data-testid="share-gate-verify-btn">View report</button>
            <button type="button" className="iv-btn iv-btn-ghost iv-btn-sm" onClick={() => { setSent(false); setCode(''); }} data-testid="share-gate-change-email">Use a different email</button>
          </>
        )}
      </div>
    </div>
  );
}

const IntegritySummary = ({ i }) => (
  <Section title="Integrity summary" testId="shared-integrity">
    <div className="iv-row"><TierPill tier={i.tier?.key} score={i.risk_score} testId="shared-integrity-tier" /><span className="iv-small">{i.label}</span>{i.determination && <Pill tone="sky">{i.determination}</Pill>}</div>
    {i.signals.length > 0 ? (
      <ul className="iv-small" style={{ paddingLeft: '1.1rem', marginTop: '0.6rem' }}>{i.signals.map((s) => <li key={s.label}>{s.label} × {s.count}</li>)}</ul>
    ) : <p className="iv-small" style={{ marginTop: '0.5rem' }}>No integrity signals were raised.</p>}
    <p className="iv-small" style={{ marginTop: '0.5rem' }}>Signals are prompts for human review, never automatic decisions. Evidence images stay inside CHIRE.</p>
  </Section>
);

export default function SharedAIReportPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [session, setSession] = useState(() => sessionStorage.getItem(KEY(token)));
  const seekRef = useRef(() => {});
  const load = useCallback((s) => shareAPI.view(token, s).then(({ data: v }) => setData(v))
    .catch((e) => setError(errMsg(e, 'This link is not available'))), [token]);
  useEffect(() => { load(session); }, [load, session]);
  const loaders = useMemo(() => ({ parts: () => shareAPI.recordingParts(token, session), part: (_, p) => shareAPI.recording(token, session, p) }), [token, session]);

  if (error) return <Shell><div className="iv-card" style={{ maxWidth: 520, margin: '2rem auto' }} data-testid="shared-report-error"><h1 className="iv-h2">Report unavailable</h1><p className="iv-sub">{error}</p></div></Shell>;
  if (!data) return <Shell><p className="iv-small">Loading…</p></Shell>;
  if (data.verification_required) return <Shell meta={data}><Gate token={token} meta={data} onVerified={setSession} /></Shell>;
  const d = { ...data, id: token };
  return (
    <Shell meta={data}>
      <div className="iv-eyebrow">{data.job_title}{data.company_name ? ` · ${data.company_name}` : ''}</div>
      <h1 className="iv-rtitle" data-testid="shared-candidate-name">{data.candidate_name}</h1>
      <p className="iv-small" style={{ margin: '0.3rem 0 1rem' }}>AI interview · completed {fmtDate(data.completed_at)} · shared by {data.shared_by || 'the hiring team'}{data.viewer_email ? ` · viewing as ${data.viewer_email}` : ''}</p>
      <AdvisoryBanner report={data.report} />
      <div className="iv-grid2">
        <div>
          <CompetencyScores d={d} onSeek={(t) => seekRef.current(t)} readOnly />
          <ClaimMatrix report={data.report} onSeek={(t) => seekRef.current(t)} />
          <InsightsPanel report={data.report} />
        </div>
        <div>
          <RecordingTranscript id={token} turns={data.transcript} seekRef={seekRef} loaders={loaders} recording={data.include_recording} />
          <IntegritySummary i={data.integrity} />
        </div>
      </div>
    </Shell>
  );
}
